import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-[#0D1B2A]/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// ─── Shared "Enter submits the dialog" behavior ─────────────────────
// One consistent keyboard rule for EVERY dialog in the app:
//   • Enter in a normal text input → triggers the dialog's primary
//     action (Save / Create / Add).
//   • Enter in a <textarea>, on a button/link, in a Radix Select /
//     combobox / search box, or when a child already handled it
//     (e.defaultPrevented, e.g. address autocomplete picking a
//     suggestion) → left alone.
//   • Any input can opt out with data-no-enter-submit; a whole dialog
//     can opt out with the enterSubmit={false} prop.
const SKIP_INPUT_TYPES = ["checkbox", "radio", "button", "submit", "reset", "file", "range", "search"];

function shouldSkipEnter(t) {
  if (!t || !t.tagName) return true;
  const tag = t.tagName;
  if (tag !== "INPUT") return true; // only plain inputs auto-submit
  if (t.isContentEditable) return true;
  const type = (t.getAttribute("type") || "text").toLowerCase();
  if (SKIP_INPUT_TYPES.includes(type)) return true;
  const role = t.getAttribute("role");
  if (role === "combobox" || role === "searchbox" || role === "menuitem") return true;
  if (t.closest && t.closest("[data-no-enter-submit]")) return true;
  return false;
}

function findPrimaryButton(container) {
  if (!container) return null;
  // 1) Explicit opt-in wins.
  let btn = container.querySelector("button[data-primary-action]:not([disabled])");
  if (btn) return btn;
  // 2) A real submit button.
  btn = container.querySelector('button[type="submit"]:not([disabled])');
  if (btn) return btn;
  const isActionable = (b) => {
    const label = (b.textContent || "").trim().toLowerCase();
    if (!label) return false; // icon-only (e.g. the close "X")
    return !/^(cancel|close|back|discard|dismiss|never ?mind|not now)$/.test(label);
  };
  // 3) Prefer the footer's last actionable button.
  const footer = container.querySelector("[data-dialog-footer]");
  if (footer) {
    const fBtns = Array.from(footer.querySelectorAll("button:not([disabled])")).filter(isActionable);
    if (fBtns.length) return fBtns[fBtns.length - 1];
  }
  // 4) Fallback: last actionable button anywhere in the dialog.
  const all = Array.from(container.querySelectorAll("button:not([disabled])")).filter(isActionable);
  return all.length ? all[all.length - 1] : null;
}

const DialogContent = React.forwardRef(({ className, overlayClassName, children, enterSubmit = true, onKeyDown, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={ref}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (enterSubmit === false) return;
        if (e.key !== "Enter" || e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
        if (e.defaultPrevented) return;
        if (shouldSkipEnter(e.target)) return;
        // If this input lives in a real <form> with its own submit
        // button, let the browser submit natively (avoids double-fire).
        if (e.target.form && e.target.form.querySelector('button[type="submit"], button[data-primary-action]')) return;
        const primary = findPrimaryButton(e.currentTarget);
        if (!primary) return;
        e.preventDefault();
        primary.click();
      }}
      className={cn(
        // GLOBAL DIALOG SAFETY CAP:
        //   * `max-h-[90vh]` keeps the dialog inside the viewport no
        //     matter how tall the body is. Without it, vertically-
        //     centered dialogs (top-[50%] translate-y-[-50%]) clip their
        //     top half above the browser window on shorter screens —
        //     users see headers / quota badges floating off-screen.
        //   * `overflow-y-auto` lets tall content scroll inside the
        //     dialog instead of overflowing the viewport.
        // Both can still be overridden by callers passing custom
        // `className` (cn() resolves the last value wins for conflicting
        // Tailwind classes via tailwind-merge).
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg max-h-[90vh] overflow-y-auto translate-x-[-50%] translate-y-[-50%] gap-4 border bg-card text-foreground p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    data-dialog-footer=""
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
